/**
 * Короткие сочетания из заданного набора символов.
 *
 * Сочетание — это движение, а не текст: три символа запоминаются рукой как
 * один переход. Собирается оно из одного набора намеренно — кусок, где два
 * символа новые, а третий старый, не является ни освоением нового движения,
 * ни повторением старого. Он просто сбивает (ТЗ FR-EN-64).
 *
 * Живёт в пакете сборки упражнений, потому что этим пользуются и «Основы», и
 * «Цифры»: две копии правила разошлись бы при первой же правке.
 */

import type { Rng } from './rng.js';

/**
 * Собирает сочетание заданной длины из одного набора.
 *
 * Три одинаковых символа подряд не составляются: это удержание клавиши, а не
 * движение. Если набор состоит из одного символа, деться от повтора некуда —
 * такой набор дополняется заранее, см. `chunkAlphabet`.
 */
export function buildChunk(rng: Rng, alphabet: readonly string[], length: number): string {
  const symbols: string[] = [];

  for (let index = 0; index < length; index += 1) {
    let symbol = rng.pick(alphabet);

    const repeats =
      symbols[symbols.length - 1] === symbol && symbols[symbols.length - 2] === symbol;
    if (repeats) {
      const others = alphabet.filter((item) => item !== symbol);
      symbol = rng.pick(others.length > 0 ? others : alphabet);
    }

    symbols.push(symbol);
  }

  return symbols.join('');
}

/**
 * Сколько раз одно сочетание можно повторить, не превращая занятие в петлю.
 *
 * То же число, что и предел показов слова в «Моих ошибках» (ТЗ FR-EN-06):
 * после третьего повтора человек не осваивает движение, а отбывает срок.
 */
export const MAX_CHUNK_REPEATS = 3;

/**
 * Сколько разных сочетаний даёт набор.
 *
 * Считается точно, а не как `A^L`: три одинаковых символа подряд `buildChunk`
 * не составляет, и такие сочетания в наборе не существуют. Разбор по
 * состоянию «сколько одинаковых символов в хвосте»: один или два.
 */
export function distinctChunks(alphabetSize: number, length: number): number {
  if (alphabetSize <= 0 || length <= 0) return 0;

  let single = alphabetSize;
  let doubled = 0;

  for (let position = 1; position < length; position += 1) {
    const nextSingle = (single + doubled) * (alphabetSize - 1);
    doubled = single;
    single = nextSingle;
  }

  return single + doubled;
}

/**
 * Предел учебных сочетаний в одном занятии.
 *
 * Занятие меряется временем (ТЗ FR-EN-60), а сочетания из двух букв коротки,
 * и в минуту их помещается сорок. Разных среди них всего шесть: набор из двух
 * букв больше не даёт. Сорок сочетаний из шести — это не освоение движения, а
 * петля, и владелец принёс её с живого использования словами «упражнение
 * показалось длинным».
 *
 * Поэтому учебных сочетаний не больше, чем набор способен дать без повторения
 * чаще трёх раз; остаток занятия занимает повторение открытых клавиш, которое
 * ТЗ FR-EN-64 разрешает прямо. Время занятия при этом не меняется.
 */
export function learningChunkLimit(alphabet: readonly string[], length: number): number {
  return distinctChunks(alphabet.length, length) * MAX_CHUNK_REPEATS;
}

/**
 * Набор для учебного сочетания: осваиваемая группа, дополненная до движения.
 *
 * Группа из одного символа даёт «яяя» — удержание клавиши. В неё добавляются
 * последние из уже открытых: они и осваивались последними, то есть ближе
 * всего к тому, что рука делает сейчас.
 */
export function chunkAlphabet(
  group: readonly string[],
  known: readonly string[],
  minSize: number,
): readonly string[] {
  if (group.length >= minSize) return group;
  return [...group, ...known.slice(-(minSize - group.length))];
}
